import { Tabs } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { PerProfileHeader } from '../../components/custom_components/Profile/ProfileHeader'
import { useAuth } from '../../context/auth-context'
import useReplyerMeetings from '../../graphql/meeting/hooks/useReplyerMeetings'
import usePublicProfile from '../../graphql/profiles/usePublicProfile'
import { useCollapse } from '../../routes/collapse-context'
import { dateSorter } from '../../shared/sortUtils'
import { useDateTime } from '../../shared/useDateTime'
import { EmployeeDisplayTinyInfo, MeetingDashboardFragment, MeetingHrFeedbackResult, MeetingStatus, MeetingType } from '../../types'
import { OwnGrowthEvaluationContainer } from '../ownGrowth/OwnGrowthEvaluationContainer'
import { EmployeeTargetsContainer } from '../targets/EmployeeTargetsContainer'
import { PublicMeetingsTab } from './tabs/PublicMeetingsTab'
import { PublicOverviewTab } from './tabs/PublicOverviewTab'

const { TabPane } = Tabs

export type ProfileTab = 'overview' | 'personalData' | 'targets' | 'meetings' | 'contract' | 'notes'

interface PublicProfilePage {
  employeeId: string
}
export const PublicProfilePage: FC<PublicProfilePage> = ({ employeeId }) => {
  const [activeKey, setActiveKey] = useState('1')
  const { user } = useAuth()
  const { utcDateStringToMoment } = useDateTime(user)
  const collapsed = useCollapse()

  const { data, error, loading } = usePublicProfile(employeeId)
  const { data: meetingsData, error: meetingsError, loading: meetingsLoading } = useReplyerMeetings(employeeId, true)
  const { t } = useTranslation()

  if (
    loading ||
    meetingsLoading ||
    error ||
    meetingsError ||
    !data ||
    !data.publicProfile ||
    !meetingsData ||
    !meetingsData.meetingsByReplyer
  ) {
    return (
      <ContentContainer collapsed={collapsed}>
        <BackgroundBox r height="fullPage">
          <NewLoadingAndErrors
            isLoading={loading || meetingsLoading}
            error={error}
            hasNoData={!data || !data.publicProfile}
            showSpinner
            t={t}
          />
        </BackgroundBox>
      </ContentContainer>
    )
  }

  const replyerMeetings: MeetingDashboardFragment[] = meetingsData.meetingsByReplyer

  const sortedMeetingData = [...replyerMeetings]
    .filter((meeting) => meeting.status !== MeetingStatus.Canceled)
    .filter((meeting) => [MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(meeting.type))
    .filter((meeting) => {
      return !(
        meeting.status === MeetingStatus.DisputedConfirmed &&
        meeting.appraisal &&
        meeting.appraisal.hrResult !== MeetingHrFeedbackResult.NoResult
      )
    })
    .sort((a, b) => dateSorter(utcDateStringToMoment(a.displayDate.valueOf()), utcDateStringToMoment(b.displayDate.valueOf())))

  const isMeetingScheduled: boolean = sortedMeetingData.length > 0
  const latestMeeting: MeetingDashboardFragment | undefined = isMeetingScheduled ? sortedMeetingData[0] : undefined

  const employee: EmployeeDisplayTinyInfo = {
    id: data.publicProfile.employeeId,
    firstName: data.publicProfile.chosenFirstName ? data.publicProfile.chosenFirstName : data.publicProfile.legalFirstName,
    lastName: data.publicProfile.chosenLastName ? data.publicProfile.chosenLastName : data.publicProfile.legalLastName,
    avatarUrl: data.publicProfile.avatarUrl,
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <div data-e2e-public-profile-page>
        <Tabs
          activeKey={activeKey}
          renderTabBar={() => (
            <PerProfileHeader profile={data.publicProfile}>
              <Tabs onChange={(key) => setActiveKey(key)}>
                <TabPane tab={t('profile.tabs.overview')} key="1" data-e2e-public-profile-tab="overview" />
                <TabPane tab={t('profile.tabs.targets')} key="2" data-e2e-public-profile-tab="targets" />
                <TabPane tab={t('profile.tabs.evaluation')} key="3" data-e2e-public-profile-tab="evaluation" />
                <TabPane tab={t('profile.tabs.meetings')} key="4" data-e2e-public-profile-tab="meetings" />
                {/* <TabPane tab={t('profile.tabs.notes')} key="4" data-e2e-public-profile-tab="notes"></TabPane> */}
              </Tabs>
            </PerProfileHeader>
          )}
        >
          <TabPane key="1">
            <BackgroundBox r height="profileView">
              <PublicOverviewTab profile={data.publicProfile} />
            </BackgroundBox>
          </TabPane>

          <TabPane key="2">
            <BackgroundBox r height="profileView" color="transparent" noPadding noShadow>
              <EmployeeTargetsContainer
                employee={employee}
                countryOfEmployee={data.publicProfile.country}
                publicProfile
                employeeEditable={data.publicProfile.editable}
                employeeSkillStatus={data.publicProfile.skillStatus || null}
                plannedMeeting={latestMeeting}
                view="PublicProfilePage"
              />
            </BackgroundBox>
          </TabPane>

          <TabPane key="3">
            <BackgroundBox r height="profileView" color="transparent" noPadding noShadow>
              <OwnGrowthEvaluationContainer
                plannedMeeting={latestMeeting}
                employee={employee}
                employeeEditable={data.publicProfile.editable}
                employeeSkillStatus={data.publicProfile.skillStatus || null}
                view="PublicProfilePage"
              />
            </BackgroundBox>
          </TabPane>

          <TabPane key="4">
            <BackgroundBox r height="profileView" color="transparent" noPadding noShadow>
              <PublicMeetingsTab employeeId={employeeId} employee={employee} editable={data.publicProfile.editable} />
            </BackgroundBox>
          </TabPane>
        </Tabs>
      </div>
    </ContentContainer>
  )
}
